import { PhoneCall } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import {
  AUTOMATION_FOLLOW_UP_PARAM,
  AutomationStatus,
  type OutboundCallAutomation,
  useActiveAutomation,
  useAutomation,
} from '@/models/outboundCallAutomation'
import {
  type SalesLeadFilters,
  SalesLeadStatus,
  salesLeadStatusColors,
  salesLeadStatusLabels,
} from '@/models/salesLead'
import { cn } from '../../../../../lib/utils'
import { AutomationFollowUpReminderForm } from './AutomationFollowUpReminderForm'
import { OutboundCallAutomationCreate } from './OutboundCallAutomationCreate'
import { OutboundCallAutomationHistory } from './OutboundCallAutomationHistory'
import { OutboundCallAutomationManage } from './OutboundCallAutomationManage'

interface OutboundCallAutomationDialogProps {
  filters: SalesLeadFilters
}

export function OutboundCallAutomationDialog({ filters }: OutboundCallAutomationDialogProps) {
  const [open, setOpen] = useState(false)
  // Set while composing a follow-up run from a past run (INS-253).
  const [followUp, setFollowUp] = useState<OutboundCallAutomation | null>(null)
  // Set while scheduling a follow-up reminder for a past run (INS-290).
  const [remindFor, setRemindFor] = useState<OutboundCallAutomation | null>(null)
  const { data: active, isLoading } = useActiveAutomation()
  const [searchParams, setSearchParams] = useSearchParams()

  // A follow-up reminder's email/toast CTA links here with the run id — open the
  // dialog already composed as a follow-up of it, so the rep just hits start.
  // Fetched by id rather than looked up in the (paginated) history, so an older
  // run still resolves.
  const deepLinkId = searchParams.get(AUTOMATION_FOLLOW_UP_PARAM)
  const { data: deepLinkRun, isLoading: isLoadingDeepLink } = useAutomation(deepLinkId)
  // Applied once per id: a later refetch must not yank the rep back to follow-up
  // compose mode after they navigated within the open dialog.
  const appliedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!deepLinkId || isLoadingDeepLink || appliedRef.current === deepLinkId) return
    appliedRef.current = deepLinkId
    // Gone, not ours, or no longer completed — the run can't be followed up.
    if (!deepLinkRun || deepLinkRun.status !== AutomationStatus.COMPLETED) {
      toast.error("That run can't be followed up any more.")
      return
    }
    setOpen(true)
    setFollowUp(deepLinkRun)
  }, [deepLinkId, isLoadingDeepLink, deepLinkRun])

  function close() {
    setOpen(false)
    setFollowUp(null)
    setRemindFor(null)
    // Drop the deep-link param once it has been consumed, so the URL the rep is
    // left on (and any reload of it) is the plain leads page.
    if (deepLinkId) {
      const next = new URLSearchParams(searchParams)
      next.delete(AUTOMATION_FOLLOW_UP_PARAM)
      setSearchParams(next, { replace: true, preventScrollReset: true })
    }
  }

  // A follow-up re-dials Called leads; a normal run only touches Untouched leads.
  const bannerStatus = followUp ? SalesLeadStatus.CALLED : SalesLeadStatus.COLD

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button className="text-md border-green-600 bg-green-600 text-white hover:border-green-700 hover:bg-green-700">
          <PhoneCall className="mr-2 size-4" />
          Outbound call automation
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{remindFor ? 'Follow-up reminder' : 'Outbound call automation'}</DialogTitle>
        </DialogHeader>
        {!isLoading && !active && !remindFor && (
          <Banner variant="info" small>
            Only leads with{' '}
            <span
              className={cn('rounded px-1.5 py-0.5 text-xs font-medium', salesLeadStatusColors[bannerStatus])}
            >
              {salesLeadStatusLabels[bannerStatus]}
            </span>{' '}
            status will be processed.
          </Banner>
        )}
        {isLoading ? (
          <div className="text-muted-foreground py-6 text-center text-sm">Loading…</div>
        ) : remindFor ? (
          <AutomationFollowUpReminderForm run={remindFor} onDone={() => setRemindFor(null)} />
        ) : active ? (
          <OutboundCallAutomationManage automation={active} onDone={close} />
        ) : followUp ? (
          <OutboundCallAutomationCreate followUpOf={followUp} onDone={close} />
        ) : (
          <OutboundCallAutomationCreate filters={filters} onDone={close} />
        )}
        {/* Past runs stay visible; follow-up buttons only when idle (one
            automation at a time per rep, and not mid-compose). Scheduling a
            reminder is always allowed — it doesn't start anything. */}
        {!isLoading && (
          <OutboundCallAutomationHistory
            excludeId={active?.id}
            onFollowUp={!active && !followUp && !remindFor ? setFollowUp : undefined}
            onRemind={remindFor ? undefined : setRemindFor}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
